(() => {
  const boot = () => {
    const root = document.querySelector('[data-store-locator]');

    if (!root || root.dataset.ready === '1') return;

    if (!window.L || !L.markerClusterGroup) {
      return setTimeout(boot, 100);
    }

    root.dataset.ready = '1';

    const dataEl = root.querySelector('[data-sl-data]');
    const listEl = root.querySelector('[data-sl-list]');
    const searchEl = root.querySelector('[data-sl-search]');
    const countryEl = root.querySelector('[data-sl-country]');
    const statusEl = root.querySelector('[data-sl-status]');
    const geoBtn = root.querySelector('[data-sl-geolocate]');

    let stores = [];

    try {
      stores = JSON.parse(dataEl.textContent || '[]');
    } catch (e) {
      console.error('Store Locator JSON', e);
    }

    const map = L.map(
      root.querySelector('[data-sl-map]'),
      {
        zoomControl: true,
        minZoom: 2,
        worldCopyJump: true
      }
    ).setView([25, 10], 2);

    L.tileLayer(
      'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors'
      }
    ).addTo(map);

    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 48
    });

    map.addLayer(cluster);

    const markers = new Map();

    let userPos = null;
    let searchPos = null;
    let searchMode = false;
    let searchTimer = null;
    let searchRequest = 0;

    const days = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday'
    ];

    const esc = s =>
      String(s ?? '').replace(
        /[&<>'"]/g,
        c => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          "'": '&#39;',
          '"': '&quot;'
        })[c]
      );

    const normalize = s =>
      String(s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    const parseTime = s => {
      const m = String(s)
        .trim()
        .match(/^(\d{1,2})(?::(\d{2}))?$/);

      if (!m) return null;

      const h = +m[1];
      const min = +(m[2] || 0);

      return h < 24 && min < 60
        ? h * 60 + min
        : null;
    };

    const parseHours = raw => {
      raw = String(raw || '')
        .trim()
        .toLowerCase();

      if (
        !raw ||
        [
          'closed',
          'geschlossen',
          'fermé',
          'cerrado',
          'chiuso'
        ].includes(raw)
      ) {
        return [];
      }

      return raw
        .split(',')
        .map(p => p.trim())
        .map(p => {
          const a = p.split(/\s*[-–—]\s*/);

          if (a.length !== 2) return null;

          const from = parseTime(a[0]);
          const to = parseTime(a[1]);

          return from === null || to === null
            ? null
            : { from, to };
        })
        .filter(Boolean);
    };

    const hhmm = m =>
      `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

    const localNow = tz => {
      try {
        const parts = new Intl.DateTimeFormat(
          'en-GB',
          {
            timeZone: tz,
            weekday: 'short',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23'
          }
        ).formatToParts(new Date());

        const obj = Object.fromEntries(
          parts.map(p => [p.type, p.value])
        );

        const idx = {
          Sun: 0,
          Mon: 1,
          Tue: 2,
          Wed: 3,
          Thu: 4,
          Fri: 5,
          Sat: 6
        }[obj.weekday];

        return {
          day: idx,
          mins: +obj.hour * 60 + +obj.minute
        };
      } catch (e) {
        const d = new Date();

        return {
          day: d.getDay(),
          mins: d.getHours() * 60 + d.getMinutes()
        };
      }
    };

    const openState = store => {
      const now = localNow(
        store.timezone || 'Europe/Berlin'
      );

      const today = parseHours(
        store.hours?.[days[now.day]]
      );

      const current = today.find(
        x =>
          x.from <= now.mins &&
          now.mins < x.to
      );

      if (current) {
        return {
          open: true,
          text: `Open now · Closes ${hhmm(current.to)}`
        };
      }

      for (let add = 0; add < 7; add++) {
        const di = (now.day + add) % 7;

        const ints = parseHours(
          store.hours?.[days[di]]
        );

        for (const x of ints) {
          if (
            add > 0 ||
            x.from > now.mins
          ) {
            const label =
              add === 0
                ? 'Opens today'
                : add === 1
                ? 'Opens tomorrow'
                : `Opens ${
                    days[di][0].toUpperCase() +
                    days[di].slice(1)
                  }`;

            return {
              open: false,
              text: `Closed · ${label} ${hhmm(x.from)}`
            };
          }
        }
      }

      return {
        open: false,
        text: 'Closed'
      };
    };

    const cityLine = s =>
      `${[s.zip, s.city]
        .filter(Boolean)
        .join(' ')}${
        s.state_region
          ? `, ${s.state_region}`
          : ''
      }`;

    const address = s =>
      [
        s.street,
        cityLine(s),
        s.country
      ]
        .filter(Boolean)
        .join(', ');

    const addressHtml = s =>
      [
        s.street,
        cityLine(s),
        s.country
      ]
        .filter(Boolean)
        .map(esc)
        .join('<br>');

    const directions = s =>
      `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address(s))}`;

    const hav = (a, b) => {
      const R = 6371;
      const r = x =>
        x * Math.PI / 180;

      const dLat = r(
        b.lat - a.lat
      );

      const dLon = r(
        b.lng - a.lng
      );

      const q =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(r(a.lat)) *
          Math.cos(r(b.lat)) *
          Math.sin(dLon / 2) ** 2;

      return 2 * R *
        Math.asin(Math.sqrt(q));
    };

    const countryCodes = {
      Germany: 'de',
      Deutschland: 'de',
      Netherlands: 'nl',
      Niederlande: 'nl',
      Austria: 'at',
      Belgium: 'be',
      Croatia: 'hr',
      Czechia: 'cz',
      Denmark: 'dk',
      Finland: 'fi',
      France: 'fr',
      Greece: 'gr',
      Hungary: 'hu',
      Ireland: 'ie',
      Italy: 'it',
      Luxembourg: 'lu',
      Norway: 'no',
      Poland: 'pl',
      Portugal: 'pt',
      Romania: 'ro',
      Slovakia: 'sk',
      Slovenia: 'si',
      Spain: 'es',
      Sweden: 'se',
      Switzerland: 'ch',
      'United Kingdom': 'gb',
      'United States': 'us'
    };

    const timezoneCountry = {
      'Europe/Berlin': 'de',
      'Europe/Amsterdam': 'nl',
      'Europe/Vienna': 'at',
      'Europe/Brussels': 'be',
      'Europe/Zagreb': 'hr',
      'Europe/Prague': 'cz',
      'Europe/Copenhagen': 'dk',
      'Europe/Helsinki': 'fi',
      'Europe/Paris': 'fr',
      'Europe/Athens': 'gr',
      'Europe/Budapest': 'hu',
      'Europe/Dublin': 'ie',
      'Europe/Rome': 'it',
      'Europe/Luxembourg': 'lu',
      'Europe/Oslo': 'no',
      'Europe/Warsaw': 'pl',
      'Europe/Lisbon': 'pt',
      'Europe/Bucharest': 'ro',
      'Europe/Bratislava': 'sk',
      'Europe/Ljubljana': 'si',
      'Europe/Madrid': 'es',
      'Europe/Stockholm': 'se',
      'Europe/Zurich': 'ch',
      'Europe/London': 'gb'
    };

    const browserCountryCode = () => {
      if (
        countryEl.value &&
        countryCodes[countryEl.value]
      ) {
        return countryCodes[
          countryEl.value
        ];
      }

      const tz =
        Intl.DateTimeFormat()
          .resolvedOptions()
          .timeZone || '';

      if (timezoneCountry[tz]) {
        return timezoneCountry[tz];
      }

      const lang =
        (
          navigator.languages?.[0] ||
          navigator.language ||
          ''
        ).toLowerCase();

      const region =
        lang.match(
          /[-_]([a-z]{2})$/
        )?.[1];

      if (region) {
        return region === 'uk'
          ? 'gb'
          : region;
      }

      if (lang.startsWith('de')) {
        return 'de';
      }

      if (lang.startsWith('nl')) {
        return 'nl';
      }

      if (lang.startsWith('fr')) {
        return 'fr';
      }

      if (lang.startsWith('es')) {
        return 'es';
      }

      if (lang.startsWith('it')) {
        return 'it';
      }

      return root.dataset
        .visitorCountry
        ? root.dataset
            .visitorCountry
            .toLowerCase()
        : '';
    };

    const geocodeText =
      async (
        q,
        countryCode = ''
      ) => {
        try {
          const params =
            new URLSearchParams({
              format: 'json',
              limit: '1',
              addressdetails: '1',
              q
            });

          if (countryCode) {
            params.set(
              'countrycodes',
              countryCode
            );
          }

          const res =
            await fetch(
              'https://nominatim.openstreetmap.org/search?' +
              params.toString(),
              {
                headers: {
                  Accept:
                    'application/json'
                }
              }
            );

          const rows =
            await res.json();

          return rows[0]
            ? {
                lat:
                  +rows[0].lat,
                lng:
                  +rows[0].lon,
                display:
                  rows[0]
                    .display_name ||
                  ''
              }
            : null;
        } catch (e) {
          return null;
        }
      };

    const hasStoredCoordinates = store => {
      const lat = Number(store.latitude);
      const lng = Number(store.longitude);

      return (
        store.latitude !== null &&
        store.latitude !== '' &&
        store.longitude !== null &&
        store.longitude !== '' &&
        Number.isFinite(lat) &&
        Number.isFinite(lng)
      );
    };

    const geocode = async store => {
      // 1. Shopify-Koordinaten: SOFORT
      if (hasStoredCoordinates(store)) {
        return {
          lat: Number(store.latitude),
          lng: Number(store.longitude),
          source: 'stored'
        };
      }

      // 2. Browser-Cache: ebenfalls SOFORT
      const key =
        'sl_geo_' +
        normalize(
          address(store)
        );

      try {
        const cached =
          JSON.parse(
            localStorage.getItem(
              key
            ) || 'null'
          );

        if (
          cached?.lat &&
          cached?.lng
        ) {
          return {
            lat: Number(cached.lat),
            lng: Number(cached.lng),
            source: 'cache'
          };
        }
      } catch (e) {}

      // 3. Nur als Fallback Nominatim
      const pos =
        await geocodeText(
          address(store)
        );

      if (pos) {
        try {
          localStorage.setItem(
            key,
            JSON.stringify(pos)
          );
        } catch (e) {}

        return {
          ...pos,
          source: 'nominatim'
        };
      }

      return null;
    };

    // Koordinaten direkt beim Start setzen,
    // damit Suche / Distanz ebenfalls sofort damit arbeiten können.
    stores.forEach(store => {
      if (hasStoredCoordinates(store)) {
        store.lat =
          Number(store.latitude);

        store.lng =
          Number(store.longitude);
      }
    });

    const popupHtml = s => {
      const o =
        openState(s);

      return `
        <div class="sl-popup">

          ${
            s.image
              ? `<img class="sl-popup__image" src="${esc(s.image)}" alt="">`
              : ''
          }

          <h3 class="sl-popup__name">
            ${esc(s.name)}
          </h3>

          <p class="sl-popup__address">
            ${addressHtml(s)}
          </p>

          <div class="sl-popup__open">
            ${esc(o.text)}
          </div>

          <div class="sl-popup__links">

            ${
              s.phone
                ? `<a href="tel:${esc(s.phone)}">${esc(s.phone)}</a>`
                : ''
            }

            ${
              s.email
                ? `<a href="mailto:${esc(s.email)}">${esc(s.email)}</a>`
                : ''
            }

            ${
              s.website
                ? `<a href="${esc(s.website)}" target="_blank" rel="noopener">Website</a>`
                : ''
            }

          </div>

          <a
            class="sl-popup__directions"
            href="${directions(s)}"
            target="_blank"
            rel="noopener"
          >
            Directions
          </a>

        </div>
      `;
    };

    const pinSvg = `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="34"
        viewBox="0 0 24 34"
      >
        <path
          fill="#111"
          d="M12 0C5.373 0 0 5.373 0 12c0 9 12 22 12 22s12-13 12-22C24 5.373 18.627 0 12 0z"
        />
        <circle
          cx="12"
          cy="12"
          r="4.5"
          fill="#fff"
        />
      </svg>
    `;

    const pinIcon =
      L.icon({
        iconUrl:
          'data:image/svg+xml;charset=UTF-8,' +
          encodeURIComponent(
            pinSvg
          ),
        iconSize: [24, 34],
        iconAnchor: [12, 34],
        popupAnchor: [0, -30],
        className:
          'sl-pin-icon'
      });

    const bottleIcon =
      L.icon({
        iconUrl:
          'https://cdn.shopify.com/s/files/1/0767/3672/5229/files/SLSVG.svg?v=1787752231',
        iconSize: [30, 47],
        iconAnchor: [15, 43],
        popupAnchor: [0, -39],
        className:
          'sl-bottle-icon'
      });

    const PIN_ZOOM = 8;

    const iconForZoom =
      () =>
        map.getZoom() >=
        PIN_ZOOM
          ? pinIcon
          : bottleIcon;

    let markerMode =
      map.getZoom() >= PIN_ZOOM
        ? 'pin'
        : 'bottle';

    const updateMarkerIcons =
      () => {
        const mode =
          map.getZoom() >=
          PIN_ZOOM
            ? 'pin'
            : 'bottle';

        if (
          mode === markerMode
        ) {
          return;
        }

        markerMode = mode;

        const nextIcon =
          mode === 'pin'
            ? pinIcon
            : bottleIcon;

        markers.forEach(
          marker =>
            marker.setIcon(
              nextIcon
            )
        );
      };

    map.on(
      'zoomend',
      updateMarkerIcons
    );

    const selectStore = s => {
      if (
        !s ||
        !Number.isFinite(
          +s.lat
        ) ||
        !Number.isFinite(
          +s.lng
        )
      ) {
        return;
      }

      const m =
        markers.get(
          String(s.id)
        );

      root
        .querySelectorAll(
          '.sl__card'
        )
        .forEach(x =>
          x.classList.toggle(
            'is-active',
            String(
              x.dataset.id
            ) ===
              String(s.id)
          )
        );

      if (!m) return;

      const parent =
        cluster.getVisibleParent
          ? cluster.getVisibleParent(
              m
            )
          : m;

      if (
        parent &&
        parent !== m &&
        parent.getLatLng
      ) {
        map.setView(
          parent.getLatLng(),
          Math.min(
            Math.max(
              map.getZoom() +
                2,
              8
            ),
            14
          ),
          {
            animate: true
          }
        );

        setTimeout(
          () => {
            if (
              map.hasLayer(m)
            ) {
              m.openPopup();
            } else {
              map.setView(
                [
                  +s.lat,
                  +s.lng
                ],
                15,
                {
                  animate:
                    true
                }
              );
            }
          },
          350
        );
      } else {
        map.setView(
          [
            +s.lat,
            +s.lng
          ],
          Math.max(
            map.getZoom(),
            15
          ),
          {
            animate: true
          }
        );

        setTimeout(
          () =>
            m.openPopup(),
          200
        );
      }
    };

    const addMarkers =
      async () => {
        statusEl.textContent =
          stores.length
            ? 'Locating stores…'
            : 'No stores available.';

        let found = 0;

        for (
          const s of stores
        ) {
          const pos =
            await geocode(s);

          if (!pos) continue;

          s.lat = pos.lat;
          s.lng = pos.lng;

          const marker =
            L.marker(
              [
                s.lat,
                s.lng
              ],
              {
                icon:
                  iconForZoom(),
                riseOnHover:
                  true,
                riseOffset:
                  500
              }
            ).bindPopup(
              () =>
                popupHtml(s),
              {
                maxWidth:
                  300
              }
            );

          marker.on(
            'popupopen',
            () =>
              root
                .querySelectorAll(
                  '.sl__card'
                )
                .forEach(x =>
                  x.classList.toggle(
                    'is-active',
                    String(
                      x.dataset.id
                    ) ===
                      String(
                        s.id
                      )
                  )
                )
          );

          markers.set(
            String(s.id),
            marker
          );

          cluster.addLayer(
            marker
          );

          found++;

          // Nur wenn wirklich Nominatim benutzt wurde,
          // warten wir 1050 ms.
          if (
            pos.source ===
            'nominatim'
          ) {
            await new Promise(
              r =>
                setTimeout(
                  r,
                  1050
                )
            );
          }
        }

        statusEl.textContent =
          `${found} store${
            found === 1
              ? ''
              : 's'
          } found`;

        render();
      };

    const textMatches =
      q =>
        stores.filter(
          s =>
            !q ||
            normalize(
              [
                s.name,
                s.street,
                s.zip,
                s.city,
                s.state_region,
                s.country
              ].join(' ')
            ).includes(q)
        );

    const filtered =
      () => {
        const q =
          normalize(
            searchEl.value
          );

        const country =
          countryEl.value;

        let arr =
          stores.filter(
            s =>
              !country ||
              s.country ===
                country
          );

        if (
          searchMode &&
          searchPos
        ) {
          arr = [...arr]
            .filter(
              s =>
                Number.isFinite(
                  +s.lat
                ) &&
                Number.isFinite(
                  +s.lng
                )
            )
            .sort(
              (a, b) =>
                hav(
                  searchPos,
                  a
                ) -
                hav(
                  searchPos,
                  b
                )
            );
        } else if (q) {
          arr =
            arr.filter(
              s =>
                normalize(
                  [
                    s.name,
                    s.street,
                    s.zip,
                    s.city,
                    s.state_region,
                    s.country
                  ].join(' ')
                ).includes(q)
            );
        }

        if (
          userPos &&
          !searchMode
        ) {
          arr = [...arr]
            .sort(
              (a, b) =>
                (
                  a.lat
                    ? hav(
                        userPos,
                        a
                      )
                    : 99999
                ) -
                (
                  b.lat
                    ? hav(
                        userPos,
                        b
                      )
                    : 99999
                )
            );
        }

        return arr;
      };

    const render = () => {
      const arr =
        filtered();

      listEl.innerHTML =
        arr.length
          ? arr
              .map(s => {
                const o =
                  openState(s);

                const basePos =
                  searchMode &&
                  searchPos
                    ? searchPos
                    : userPos;

                const dist =
                  basePos &&
                  s.lat
                    ? hav(
                        basePos,
                        s
                      )
                    : null;

                return `
                  <article
                    class="sl__card"
                    data-id="${esc(String(s.id))}"
                  >

                    <h3 class="sl__card-name">
                      ${esc(s.name)}
                    </h3>

                    <p class="sl__address">
                      ${addressHtml(s)}
                    </p>

                    <p
                      class="sl__open ${
                        o.open
                          ? 'is-open'
                          : ''
                      }"
                    >
                      ${esc(o.text)}
                    </p>

                    ${
                      dist !== null
                        ? `<p class="sl__distance">${dist.toFixed(1)} km away</p>`
                        : ''
                    }

                    <div class="sl__actions">

                      <button
                        type="button"
                        data-show-map
                      >
                        Show on map
                      </button>

                      <a
                        href="${directions(s)}"
                        target="_blank"
                        rel="noopener"
                      >
                        Directions
                      </a>

                    </div>

                  </article>
                `;
              })
              .join('')
          : `
            <div class="sl__empty">
              No matching stores found.
            </div>
          `;

      const visibleById =
        new Map(
          arr.map(s => [
            String(s.id),
            s
          ])
        );

      listEl
        .querySelectorAll(
          '.sl__card'
        )
        .forEach(card => {
          card.addEventListener(
            'click',
            e => {
              if (
                e.target.closest(
                  'a'
                )
              ) {
                return;
              }

              const s =
                visibleById.get(
                  String(
                    card.dataset.id
                  )
                );

              if (s) {
                selectStore(s);
              }
            }
          );
        });

      if (
        searchMode &&
        searchPos
      ) {
        const within =
          arr.filter(
            s =>
              hav(
                searchPos,
                s
              ) <= 50
          ).length;

        statusEl.textContent =
          within
            ? `${within} store${within === 1 ? '' : 's'} within 50 km · nearest first`
            : 'No stores within 50 km · nearest locations:';
      } else {
        statusEl.textContent =
          `${arr.length} store${arr.length === 1 ? '' : 's'} shown`;
      }
    };

    const runSearch =
      async () => {
        const raw =
          searchEl.value.trim();

        const q =
          normalize(raw);

        searchMode = false;
        searchPos = null;

        if (!raw) {
          render();
          return;
        }

        const direct =
          textMatches(q)
            .filter(
              s =>
                !countryEl.value ||
                s.country ===
                  countryEl.value
            );

        if (
          direct.length
        ) {
          render();
          return;
        }

        const request =
          ++searchRequest;

        statusEl.textContent =
          'Searching location…';

        const pureZip =
          /^\d{4,6}$/.test(
            raw.replace(
              /\s/g,
              ''
            )
          );

        const cc =
          browserCountryCode();

        let pos =
          await geocodeText(
            raw,
            pureZip
              ? cc
              : countryEl.value
              ? cc
              : ''
          );

        if (
          request !==
          searchRequest
        ) {
          return;
        }

        if (
          !pos &&
          !pureZip
        ) {
          pos =
            await geocodeText(
              raw
            );
        }

        if (pos) {
          searchPos =
            pos;

          searchMode =
            true;

          map.setView(
            [
              pos.lat,
              pos.lng
            ],
            9,
            {
              animate: true
            }
          );

          render();
        } else {
          render();

          statusEl.textContent =
            'No matching location found.';
        }
      };

    [
      ...new Set(
        stores
          .map(
            s =>
              s.country
          )
          .filter(Boolean)
      )
    ]
      .sort()
      .forEach(
        c =>
          countryEl.add(
            new Option(
              c,
              c
            )
          )
      );

    searchEl.addEventListener(
      'input',
      () => {
        clearTimeout(
          searchTimer
        );

        searchTimer =
          setTimeout(
            runSearch,
            650
          );
      }
    );

    countryEl.addEventListener(
      'change',
      () => {
        if (
          searchEl.value.trim()
        ) {
          runSearch();
        } else {
          render();
        }
      }
    );

    geoBtn.addEventListener(
      'click',
      () => {
        if (
          !navigator.geolocation
        ) {
          statusEl.textContent =
            'Location is not supported by this browser.';

          return;
        }

        statusEl.textContent =
          'Getting your location…';

        navigator.geolocation
          .getCurrentPosition(
            p => {
              userPos = {
                lat:
                  p.coords.latitude,
                lng:
                  p.coords.longitude
              };

              searchMode =
                false;

              searchPos =
                null;

              map.setView(
                [
                  userPos.lat,
                  userPos.lng
                ],
                10
              );

              L.circleMarker(
                [
                  userPos.lat,
                  userPos.lng
                ],
                {
                  radius: 7
                }
              )
                .addTo(map)
                .bindPopup(
                  'Your location'
                );

              render();
            },
            () =>
              statusEl.textContent =
                'Location access was not granted.',
            {
              enableHighAccuracy:
                false,
              timeout: 8000
            }
          );
      }
    );

    render();
    addMarkers();
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot
    );
  } else {
    boot();
  }
})();